import { InputNumber } from 'antd';
import type { InputNumberProps } from 'antd';
import type { InputNumberRef } from '@rc-component/input-number';
import { forwardRef, useCallback } from 'react';
import type { KeyboardEvent, ClipboardEvent } from 'react';
import type { InputContext } from './TextInput';
import { filterNumberInputKey, handleNumberInputPaste } from './keyFilter';
import { CONTROL_CLASS } from '@/theme/controlSizing';

/**
 * Props {@link CounterInput}.
 *
 * Tidak meng-expose `formatter`/`parser`/`controls`/`precision` karena sudah
 * dikunci sesuai spek Counter. Properti lain diteruskan ke Antd `<InputNumber>`.
 */
export interface CounterInputProps
  extends Omit<
    InputNumberProps<number>,
    'formatter' | 'parser' | 'controls' | 'precision' | 'size' | 'stringMode'
  > {
  /** Konteks pemakaian — menentukan density. Default: `form`. */
  context?: InputContext;
  /** Override paksa size Antd jika perlu. */
  size?: InputNumberProps['size'];
}

/**
 * SIBLING dari `NumberInput`: Textbox untuk **counter** / index integer
 * (mis. AngsuranKe, Jumlah Hari, Term of Payment).
 *
 * Spek (BERBEDA dengan {@link NumberInput}/`IDRInput`):
 * - Underlying: Antd `<InputNumber>`.
 * - Integer (precision = 0).
 * - **TANPA stepper** (`controls={false}`) — kebijakan Booku V2: tidak ada
 *   panah ▲▼ di kolom angka mana pun. Lihat textbox-pattern.md section
 *   "KEBIJAKAN: Tanpa Stepper".
 * - **TANPA separator ribuan** — counter adalah index, bukan magnitude.
 *   Tampilan: `1`, `2`, `100`, `1234`.
 * - **Nilai 0 ditampilkan apa adanya** (`'0'`), TIDAK dikosongkan.
 *   Counter `0` punya makna (mis. "0 hari = jatuh tempo hari ini").
 * - Rata kanan + width 100% (konsisten dengan family).
 *
 * **Kapan pakai ini, bukan `IDRInput`/`QuantityInput`?**
 * - `CounterInput` → integer index/urutan/term (1, 2, 3, ...) yang nilai 0
 *   adalah valid dan berbeda makna dari "kosong".
 * - `IDRInput` → nilai uang Rupiah (boleh kosong, separator ribuan).
 * - `QuantityInput` → kuantitas barang (boleh kosong, separator ribuan).
 *
 * Pemakaian:
 * ```tsx
 * <CounterInput value={angsuranKe} onChange={setAngsuranKe} min={1} max={999} />
 * <CounterInput value={jumlahHari} onChange={setJumlahHari} min={0} addonAfter="hari" />
 * ```
 *
 * Dokumentasi pattern: `.claude/docs/Booku v2/ui-patterns/textbox-pattern.md`
 */
export const CounterInput = forwardRef<InputNumberRef, CounterInputProps>(
  function CounterInput(
    { context = 'form', size, style, className, onKeyDown, onPaste, ...rest },
    ref
  ) {
    const resolvedSize = size ?? (context === 'page' ? 'small' : 'middle');
    // Counter = integer (no decimal/comma). Minus tetap diizinkan (rule
    // global: di awal, hanya satu) — komponen yang non-negatif set `min={0}`.
    const filterOptions = { allowComma: false };

    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        if (filterNumberInputKey(e, filterOptions)) return;
        onKeyDown?.(e);
      },
      [filterOptions, onKeyDown]
    );

    const handlePaste = useCallback(
      (e: ClipboardEvent<HTMLInputElement>) => {
        handleNumberInputPaste(e, filterOptions);
        onPaste?.(e);
      },
      [filterOptions, onPaste]
    );

    return (
      <InputNumber<number>
        ref={ref}
        size={resolvedSize}
        controls={false}
        precision={0}
        // Tidak ada formatter — pakai default rendering Antd (tanpa separator).
        className={`${CONTROL_CLASS} booku-number-input${className ? ` ${className}` : ''}`}
        style={{ width: '100%', ...style }}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        {...rest}
      />
    );
  }
);
